import React, { useState, useRef, useEffect } from 'react';
import { Checkbox, withStyles } from '@material-ui/core';
import { NumericEditor, DateTimeEditor } from '@dataGrid/components/cellEditors';
import TrailerTypes from './TrailerTypes';
import ReturnToTerminalIcon from '../../../../modules/IBPlanning/Common/ReturnToTerminalIcon';
import appConstants from '../../../../../appConstants';
import {
  convertTimeToMinute,
  minutesToTime,
  isTimeValid
} from '@dataGrid/gridUtilities';
import ErrorMessage from './ErrorMessage';
import { checkIfFMERouteTrailer } from '../utils';
import { inputFields } from '../constants';

const styles = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: theme.textFontSize,
    color: theme.gridTextColor
  },
  subSectionTitle: {
    fontSize: theme.textFontSize,
    color: theme.gridTextColor,
    textTransform: 'uppercase'
  },
  checkbox: {
    padding: 0,
    marginRight: 8,
    color: theme.iconColor
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    margin: '8px 0'
  },
  inputField: {
    fontSize: theme.textFontSize,
    fontWeight: 500
  },
  inputError: {
    '&>div': {
      borderColor: theme.ErrorMessageColor
    }
  },
  returnToTerminalWrapper: { display: 'flex', alignItems: 'center' },
  returnToTerminalLabel: {
    marginRight: 8
  },
  numericEditor: {
    width: '64px',
    height: '27px'
  }
});

const OptimizationConstraintsTab = ({
  classes,
  data,
  hideReturnToTerminalOption,
  isReturnToTerminal,
  maxStopsPerRouteCount,
  lastWaveCutOff,
  errorField,
  constraintError,
  trailerTypeCountList,
  onTrailerCountChange,
  onMaxStopsPerRouteCountChange,
  onFormDataChange
}) => {
  const inputRefs = useRef({}); // Initialize an empty array of refs
  const lastWaveCutOffTimeRef = useRef(null);
  const maxStopsPerRouteCountRef = useRef(null);

  useEffect(() => {
    const refs = inputRefs.current;

    // Early return if there are no input refs
    if (!refs) return;

    const keys = Object.keys(refs); // Store keys in a variable
    if (keys.length === 0) return;

    // Default to the first input field
    let index = keys[0];

    if (data.isOnlyFMERoute) {
      // Find the first FME trailer if only FME route is selected
      const firstFMETrailerIdx = keys.find((key) =>
        checkIfFMERouteTrailer(refs[key].trailerTypeName)
      );

      // If a FME trailer exists, use its index
      if (firstFMETrailerIdx) {
        index = firstFMETrailerIdx;
      }
    }

    refs[index]?.ref?.focusOnInput(); // Focus on the determined input field
  }, [data.isOnlyFMERoute]);

  useEffect(() => {
    if (errorField === inputFields.maxStopsPerRoute) maxStopsPerRouteCountRef.current.focusOnInput();
  }, [errorField]);

  const [lastWaveCutOffValue, setLastWaveCutOffValue] = useState(() => {
    const time = isTimeValid(lastWaveCutOff)
      ? lastWaveCutOff
      : minutesToTime({ value: convertTimeToMinute(lastWaveCutOff) });
    return time;
  });
  const handleChange = (name) => (event) => {
    if (name === inputFields.maxStopsPerRoute) {
      onMaxStopsPerRouteCountChange(parseInt(event.target.value));
    } else if (name === inputFields.lastWaveCutOff) {
      const time = minutesToTime({
        value: convertTimeToMinute(lastWaveCutOffTimeRef.current.getValue())
      });
      if (time !== 'Invalid Date') {
        setLastWaveCutOffValue(time);
        onFormDataChange(name, event);
      }
    } else if (name === inputFields.isReturnToTerminal) {
      onFormDataChange(name, event.target.checked);
    }
  };

  const { liftGateTrailers, nonLiftGateTrailers } = data.trailersData.reduce(
    (result, item) => {
      if (item.isLiftGateRequired === appConstants.SpecialNeedsStrings.L) {
        result.liftGateTrailers.push(item);
      } else {
        result.nonLiftGateTrailers.push(item);
      }
      return result;
    },
    { liftGateTrailers: [], nonLiftGateTrailers: [] }
  );

   const getErrorClass = (fieldName, errorField, classes) => {
    return errorField === fieldName ? classes.inputError : '';
  };


  return (
    <div className={classes.container}>
      <div className={classes.subSectionTitle}>Select Equipment</div>
      <div>
        <TrailerTypes
          title="LG"
          trailers={liftGateTrailers}
          trailerTypeCountList={trailerTypeCountList}
          onTrailerCountChange={onTrailerCountChange}
          inputRefs={inputRefs}
          errorClass= {getErrorClass(inputFields.trailerTypeCountList, errorField, classes)}
        />
        <TrailerTypes
          title="Non-LG"
          trailers={nonLiftGateTrailers}
          trailerTypeCountList={trailerTypeCountList}
          onTrailerCountChange={onTrailerCountChange}
          inputRefs={inputRefs}
          errorClass= {getErrorClass(inputFields.trailerTypeCountList, errorField, classes)}
        />
      </div>
      <div className={classes.flexContainer}>
        <div className={classes.inputField}>
          <div>Max stops per route</div>
          <div
            className={`${classes.numericEditor} ${
              getErrorClass(inputFields.maxStopsPerRoute, errorField, classes)
            }`}>
            <NumericEditor
              ref={maxStopsPerRouteCountRef}
              value={maxStopsPerRouteCount}
              onChange={handleChange(inputFields.maxStopsPerRoute)}
            />
          </div>
        </div>
        <div className={classes.inputField}>
          <div>Last wave cutoff</div>
          <div>
            <DateTimeEditor
              ref={lastWaveCutOffTimeRef}
              dateType={appConstants.DATETYPES.TIME}
              onChange={handleChange(inputFields.lastWaveCutOff)}
              value={lastWaveCutOffValue}
              disableFocus
              disableFocusInput
            />
          </div>
        </div>
      </div>
      {!hideReturnToTerminalOption && (
        <div className={classes.returnToTerminalWrapper}>
          <Checkbox
            color="primary"
            className={classes.checkbox}
            checked={isReturnToTerminal}
            onChange={handleChange(inputFields.isReturnToTerminal)}
          />
          <span className={classes.returnToTerminalLabel}>
            Return routes directly to terminal after final delivery
          </span>
          <ReturnToTerminalIcon width={28} height={28} />
        </div>
      )}
      {constraintError && <ErrorMessage error={constraintError} />}
    </div>
  );
};

export default withStyles(styles)(OptimizationConstraintsTab);
